/**
 * Why a chef print surface has nothing to print.
 *
 * The chef kitchen-list pages build their print packet from the days in the
 * selected range that carry PLACED shipments. When that set is empty the
 * packet is `null` and there is genuinely nothing to hand the printer — but
 * the Print button used to stay enabled and its click handler simply
 * returned, so the button did nothing at all. On a phone that is
 * indistinguishable from a broken PDF export, and it sent a real chef
 * chasing a print bug that was actually an empty range (2026-08-31).
 *
 * This is the pure decision behind the button's disabled state and the
 * explanation shown next to it.
 */
export type PrintAvailability = { canPrint: true } | { canPrint: false; reason: string }

/**
 * Decide whether the current range can produce a print packet, and if not,
 * say which of the two empty cases it is:
 *
 *   - no service days in the range at all (a fully off week), versus
 *   - service days exist, but none of them has a placed order yet.
 *
 * `hasPrintTarget` is the built packet's own verdict — it already accounts
 * for VOIDED/filtered shipments — so this never re-derives that rule.
 */
export function printAvailability({
  hasPrintTarget,
  serviceDayCount,
}: {
  hasPrintTarget: boolean
  serviceDayCount: number
}): PrintAvailability {
  if (hasPrintTarget) {
    return { canPrint: true }
  }
  if (serviceDayCount === 0) {
    return { canPrint: false, reason: 'No service days in this range' }
  }
  return { canPrint: false, reason: 'No placed orders in this range yet' }
}
